import {Component, computed, effect, inject, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TableModule} from 'primeng/table';
import {Tag} from 'primeng/tag';
import {TranslocoPipe} from '@jsverse/transloco';
import {TooltipModule} from 'primeng/tooltip';
import {BackgroundCardComponent} from '../../../shared/components/titled-background-card/background-card.component';
import {ConfirmStepLeaveDirective} from '../../../shared/project-step-save-button/confirm-step-leave.directive';
import {Button} from 'primeng/button';
import {
  CustomerContractWithModulesRequest,
  CustomerContractWithModulesResponse,
  InvoiceDataResponse,
  AdminCustomerContractControllerService,
  ResendInvitationRequestParams,
} from '@frontend/shared-api';
import {ToastService} from '@frontend/toast';
import {firstValueFrom} from 'rxjs';
import {map} from 'rxjs/operators';
import {rxResource, toSignal} from '@angular/core/rxjs-interop';
import {FunctionSelectionDialogComponent} from '../../../shared/components/dialog/function-selection-dialog/function-selection-dialog.component';
import {FunctionSelectionDialogStateService} from '../../../shared/components/dialog/function-selection-dialog/function-selection-dialog-state.service';
import {InvoiceDataDialogComponent} from './invoice-data-dialog/invoice-data-dialog.component';
import {FunctionDisplayService} from '../../../shared/services/function-display.service';
import {ModuleFunctionDisplayData} from '../../../shared/models/module-function-display.model';
import {DirectusService, Module, ModuleFunction, PaymentPlan} from '@frontend/directus';
import {
  getDateTimestamp,
  isDateRangeActive,
  isEndDateBeforeStartDate,
} from '../../../shared/utils/date-utils';
import {DatePicker} from 'primeng/datepicker';
import {Checkbox} from 'primeng/checkbox';
import {PaymentPlanSelectComponent} from '../../../shared/components/payment-plan-select/payment-plan-select.component';
import {TableExpandControlsComponent} from '../../../shared/components/table/table-expand-controls/table-expand-controls.component';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {NoDataComponent} from '../../../shared/components/no-data/no-data.component';
import {SortEvent} from 'primeng/api';
import {CustomerTableData} from './selecthor-admin-customer-list.types';
import {GlobalSearchComponent} from '../../../shared/components/search/global-search/global-search.component';
import {ConfirmationDialogService} from '../../../shared/components/dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'app-selecthor-admin-customer-list',
  templateUrl: './selecthor-admin-customer-list.component.html',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Tag,
    TranslocoPipe,
    BackgroundCardComponent,
    ConfirmStepLeaveDirective,
    Button,
    FunctionSelectionDialogComponent,
    InvoiceDataDialogComponent,
    TooltipModule,
    DatePicker,
    Checkbox,
    PaymentPlanSelectComponent,
    TableExpandControlsComponent,
    LoadingComponent,
    NoDataComponent,
    GlobalSearchComponent,
  ],
  providers: [FunctionSelectionDialogStateService],
})
export class SelecthorAdminCustomerListComponent {
  private readonly adminCustomerContractController = inject(AdminCustomerContractControllerService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly toastService = inject(ToastService);
  private readonly directusService = inject(DirectusService);
  private readonly functionDisplayService = inject(FunctionDisplayService);
  protected dialogStateService = inject(FunctionSelectionDialogStateService);

  readonly saving = signal(false);
  readonly customers = signal<CustomerTableData[]>([]);
  readonly originalCustomers = signal<CustomerTableData[]>([]);
  readonly hasUnsavedChanges = signal(false);
  readonly dialogTranslationPrefix = 'selecthorAdmin.customerList.dialog';
  readonly invoiceDialogVisible = signal(false);
  readonly selectedCustomerInvoiceData = signal<InvoiceDataResponse | null>(null);
  readonly expandedRows = signal<Record<number, boolean>>({});

  private readonly moduleFunctionsByModule = toSignal(
    this.directusService.loadModuleFunctionsGroupedByModule(),
    {initialValue: new Map<number, Module>()},
  );

  readonly paymentPlans = toSignal(this.directusService.loadPaymentPlans(), {
    initialValue: [] as PaymentPlan[],
  });

  readonly allModuleFunctions = computed<ModuleFunction[]>(() => {
    const allFunctions: ModuleFunction[] = [];
    this.moduleFunctionsByModule().forEach((module) => {
      if (module.functions) {
        allFunctions.push(...module.functions);
      }
    });
    return allFunctions;
  });

  protected readonly customersResource = rxResource({
    params: () => {
      const moduleFunctions = this.allModuleFunctions();
      return moduleFunctions.length ? moduleFunctions : undefined;
    },
    stream: () =>
      this.adminCustomerContractController
        .getAllCustomersWithContracts()
        .pipe(map((apiData) => this.transformApiDataToCustomerTableData(apiData))),
  });

  readonly sortField = signal('companyName');
  readonly sortOrder = signal(1);

  readonly globalFilterFields = ['companyName', 'adminFirstName', 'adminLastName', 'adminEmail'];

  readonly hasCustomers = computed(() => this.customers().length > 0);
  readonly changedDatesValid = computed(() =>
    this.getChangedCustomers().every((customer) => !this.isEndDateBeforeStartDate(customer)),
  );
  readonly shouldShowSaveButton = computed(
    () => this.hasUnsavedChanges() && !this.saving() && this.changedDatesValid(),
  );

  constructor() {
    effect(() => {
      const data = this.customersResource.value();
      if (!data) return;

      this.customers.set(data);
      this.originalCustomers.set(structuredClone(data));
      this.hasUnsavedChanges.set(false);
    });
  }

  onSort(event: SortEvent): void {
    this.sortField.set(event.field ?? 'companyName');
    this.sortOrder.set(event.order ?? 1);
  }

  onContractDateChange(
    customer: CustomerTableData,
    field: 'contractStartDate' | 'contractEndDate',
    date: Date | null,
  ): void {
    this.customers.update((customers) =>
      customers.map((tableData) => {
        if (tableData.tenantId === customer.tenantId) {
          const updated = {...tableData, [field]: date};
          updated.status = isDateRangeActive(updated.contractStartDate, updated.contractEndDate);
          return updated;
        }
        return tableData;
      }),
    );
    this.hasUnsavedChanges.set(true);
  }

  onPaymentPlanChange(customer: CustomerTableData, planDirectusId: string | null): void {
    this.customers.update((customers) =>
      customers.map((c) =>
        c.tenantId === customer.tenantId ? {...c, paymentPlanDirectusId: planDirectusId} : c,
      ),
    );
    this.hasUnsavedChanges.set(true);
  }

  onPaidStatusChange(customer: CustomerTableData, paid: boolean): void {
    this.customers.update((customers) =>
      customers.map((c) => (c.tenantId === customer.tenantId ? {...c, paid} : c)),
    );
    this.hasUnsavedChanges.set(true);
  }

  selectFeatures(customer: CustomerTableData) {
    this.dialogStateService.openDialog(customer.tenantId, customer.companyName);
  }

  confirmResendVerificationEmail(customer: CustomerTableData): void {
    this.confirmDialogService.confirm({
      headerKey: 'selecthorAdmin.customerList.resendVerificationEmail',
      messageKey: 'selecthorAdmin.customerList.confirmResendVerificationEmail',
      accept: () => {
        void this.resendVerificationEmail(customer);
      },
    });
  }

  async resendVerificationEmail(customer: CustomerTableData): Promise<void> {
    const request: ResendInvitationRequestParams = {
      tenantId: customer.tenantId,
      customerInvitationRequest: {
        email: customer.adminEmail,
      },
    };

    await firstValueFrom(this.adminCustomerContractController.resendInvitation(request));

    await this.toastService.showSuccess(
      'selecthorAdmin.customerList.verificationEmailSent',
      'selecthorAdmin.customerList.verificationEmailSentDetail',
      {email: customer.adminEmail},
    );
  }

  viewInvoiceData(customer: CustomerTableData): void {
    if (customer.invoiceData) {
      this.selectedCustomerInvoiceData.set(customer.invoiceData);
      this.invoiceDialogVisible.set(true);
    }
  }

  onFeaturesSelected(functions: ModuleFunction[]) {
    const tenantId = this.dialogStateService.currentEntityId();
    if (tenantId !== null) {
      this.updateCustomerFeatures(tenantId, functions);
      this.dialogStateService.closeDialog();
      this.hasUnsavedChanges.set(true);
    }
  }

  private updateCustomerFeatures(tenantId: number, functions: ModuleFunction[]) {
    this.customers.update((customers) =>
      customers.map((customer) =>
        customer.tenantId === tenantId ? {...customer, selectedFeatures: [...functions]} : customer,
      ),
    );
  }

  getSelectedFeaturesForDialog(): ModuleFunction[] {
    const tenantId = this.dialogStateService.currentEntityId();
    const customer = this.customers().find((c) => c.tenantId === tenantId);
    return customer?.selectedFeatures ?? [];
  }

  getCustomerFunctionsDisplayWithTooltipData(
    customer: CustomerTableData,
  ): ModuleFunctionDisplayData[] {
    const functions = customer.selectedFeatures || [];
    if (functions.length === 0) {
      return [];
    }

    const moduleFunctionMap = this.functionDisplayService.groupFunctionsByModule(functions);
    return this.functionDisplayService.displayAsModuleNameWithFunctionsCount(moduleFunctionMap);
  }

  async saveAllChanges(): Promise<void> {
    if (!this.hasUnsavedChanges()) return;

    const changedCustomers = this.getChangedCustomers();

    if (changedCustomers.length === 0) {
      this.hasUnsavedChanges.set(false);
      return;
    }

    this.saving.set(true);

    try {
      for (const customer of changedCustomers) {
        const request: CustomerContractWithModulesRequest = {
          customerContractRequest: {
            tenantId: customer.tenantId,
            contractStartDate: customer.contractStartDate?.toISOString(),
            contractEndDate: customer.contractEndDate?.toISOString(),
          },
          moduleFunctions: customer.selectedFeatures.map((feature) => ({
            directusId: feature.id,
          })),
          selectedPlan: customer.paymentPlanDirectusId
            ? {
                directusId: customer.paymentPlanDirectusId,
                paid: customer.paid,
              }
            : undefined,
        };

        await firstValueFrom(
          this.adminCustomerContractController.upsertCustomerContract({
            tenantId: customer.tenantId,
            customerContractWithModulesRequest: request,
          }),
        );
      }

      await this.toastService.showSuccess(
        'selecthorAdmin.customerList.changesSaved',
        `${changedCustomers.length} customer contract(s) updated successfully`,
      );

      this.hasUnsavedChanges.set(false);
      this.customersResource.reload();
    } finally {
      this.saving.set(false);
    }
  }

  private hasCustomerChanged(current: CustomerTableData, original: CustomerTableData): boolean {
    if (current.tenantId !== original.tenantId) return true;

    const currentStartDate = getDateTimestamp(current.contractStartDate);
    const originalStartDate = getDateTimestamp(original.contractStartDate);
    const currentEndDate = getDateTimestamp(current.contractEndDate);
    const originalEndDate = getDateTimestamp(original.contractEndDate);

    if (currentStartDate !== originalStartDate || currentEndDate !== originalEndDate) {
      return true;
    }

    if (current.paymentPlanDirectusId !== original.paymentPlanDirectusId) {
      return true;
    }

    if (current.paid !== original.paid) {
      return true;
    }

    const currentFeatureIds = current.selectedFeatures.map((f) => f.id).sort((a, b) => a - b);
    const originalFeatureIds = original.selectedFeatures.map((f) => f.id).sort((a, b) => a - b);

    if (currentFeatureIds.length !== originalFeatureIds.length) {
      return true;
    }

    return !currentFeatureIds.every((id, index) => id === originalFeatureIds[index]);
  }

  private getChangedCustomers(): CustomerTableData[] {
    const currentCustomers = this.customers();
    const originalCustomers = this.originalCustomers();

    return currentCustomers.filter((current) => {
      const original = originalCustomers.find((orig) => orig.tenantId === current.tenantId);
      return original ? this.hasCustomerChanged(current, original) : true;
    });
  }

  private transformApiDataToCustomerTableData(
    apiData: CustomerContractWithModulesResponse[],
  ): CustomerTableData[] {
    return apiData
      .filter((response) => response.customerContractResponse.tenantInfo)
      .map((response): CustomerTableData => {
        const {customerContractResponse, moduleFunctions} = response;
        const {tenantId, tenantInfo, startDate, endDate} = customerContractResponse;
        const contractStartDate = startDate ? new Date(startDate) : null;
        const contractEndDate = endDate ? new Date(endDate) : null;

        const selectedFeatures: ModuleFunction[] =
          moduleFunctions
            ?.map((feature) =>
              this.allModuleFunctions().find((mf) => mf.id === Number(feature.directusId)),
            )
            .filter((mf): mf is ModuleFunction => mf !== undefined) ?? [];

        return {
          tenantId,
          companyName: `${tenantInfo.tenantName}`.trim(),
          adminFirstName: tenantInfo.firstName ?? '',
          adminLastName: tenantInfo.lastName ?? '',
          adminEmail: tenantInfo.email ?? '',
          adminEmailVerified: customerContractResponse.adminEmailVerified,
          status: isDateRangeActive(contractStartDate, contractEndDate),
          contractStartDate,
          contractEndDate,
          selectedFeatures,
          paymentPlanDirectusId: customerContractResponse.selectedPlan?.directusId ?? null,
          paid: customerContractResponse.selectedPlan?.paid ?? false,
          invoiceData: customerContractResponse.invoiceData,
        };
      });
  }

  isEndDateBeforeStartDate(customer: CustomerTableData): boolean {
    return isEndDateBeforeStartDate(customer.contractStartDate, customer.contractEndDate);
  }

  expandAll(): void {
    this.expandedRows.set(
      this.customers().reduce<Record<number, boolean>>((acc, customer) => {
        acc[customer.tenantId] = true;
        return acc;
      }, {}),
    );
  }

  collapseAll(): void {
    this.expandedRows.set({});
  }
}
